import i18n from '@/i18n';
import Project from '@/models/Project';
import ProjectService from '@/services/ProjectService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';
import { useCommentsStore } from './comments';

export const useProjectsStore = defineStore('projects', {
  state: () => ({
    selectedProject: undefined,
  }),

  getters: {
    models: () => useRepo(Project),

    project(state): Project {
      return this.models.find(state.selectedProject);
    },

    currentLocale(): typeof i18n['global']['availableLocales'][number] {
      return this.project?.locale as any ?? 'en-AU';
    },
  },

  actions: {
    setProject(Id: Id, navigate = false) {
      this.selectedProject = Id;

      if(navigate) {
        this.router.push({ name: this.router.currentRoute.value.name, params: { projectId: Id } });
      }
    },

    async fetchProject(id: Id) {
      const [data, error] = await ProjectService.show(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async fetchProjects() {
      const [data, error] = await ProjectService.index();

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async searchProjects(form: ApiRequests['api.v1.projects.search']) {
      const [data, error] = await ProjectService.search(form);

      if(error) {
        throw error;
      }

      return data as unknown as {
        projects: CursorPaginator<Project>;
        profit: { id: Id; profit: number; total_budget: number; total_certified: number }[];
        has_more: boolean;
      };
    },

    async updateProject(id: Id, form: ApiRequests['api.v1.projects.update']) {
      const [data, error] = await ProjectService.update(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateHeadContractSettings(id: Id, form: ApiRequests['api.v1.projects.head-contract-settings']) {
      const [data, error] = await ProjectService.updateHeadContractSettings(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteProject(id: Id) {
      const [data, error] = await ProjectService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async createProject(form: ApiRequests['api.v1.projects.store']) {
      const [data, error] = await ProjectService.create(form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async createCompanyProject(companyId: Id, form: ApiRequests['api.v1.company.projects.store']) {
      const [data, error] = await ProjectService.companyCreate(companyId, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async wbsCodeAverage(id: Id, form: ApiRequests['api.v1.projects.wbs_code.average']) {
      const [data, error] = await ProjectService.wbsCodeAverage(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async utlisationReport(id: Id, form: ApiRequests['api.v1.projects.plant-utilisation']) {
      const [data, error] = await ProjectService.utilisationReport(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async selfPerformDashboard(
      id: Id,
      form: ApiRequests['api.v1.projects.plant-utilisation-metrics'] = { date: undefined },
    ) {
      const [data, error] = await ProjectService.plantUtilisationGraph(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async selfPerformMetrics(id: Id, form: ApiRequests['api.v1.projects.self-perform-metrics'] = { date: undefined }) {
      const [data, error] = await ProjectService.selfPerformMetrics(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async weeklyPerformanceGraph(id: Id) {
      const [data, error] = await ProjectService.weeklyPerformanceGraph(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async clientMetrics(id: Id) {
      const [data, error] = await ProjectService.clientMetrics(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async seedDemo(form: ApiRequests['api.v1.admin.projects.seed-demo']) {
      const [data, error] = await ProjectService.seedDemo(form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async fetchAdminProjects(page = 1) {
      const [data, error] = await ProjectService.adminIndex(page);

      if(error) {
        throw error;
      }

      this.models.flush();
      this.models.save(data.projects.data);

      return data;
    },

    async searchAdminProjects(request: ApiRequests['api.v1.admin.projects.search']) {
      const [data, error] = await ProjectService.admin.search(request);

      if(error) {
        throw error;
      }

      return data;
    },

    async lockBoQ(id: Id) {
      const [data, error] = await ProjectService.lockBoq(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async unlockBoq(id: Id) {
      const [data, error] = await ProjectService.unlockBoq(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async storeCoverPage(id: Id, form: ApiRequests['api.v1.projects.cover-page.store'] | FormData) {
      const [data, error] = await ProjectService.coverPage.store(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async showCoverPage(id: Id) {
      const [data, error] = await ProjectService.coverPage.show(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async storeDocuments(id: Id, form: ApiRequests['api.v1.projects.documents.store'] | FormData) {
      const [data, error] = await ProjectService.documents.store(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async showDocuments(id: Id) {
      const [data, error] = await ProjectService.documents.show(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async monthlyReportsIndex(id: Id) {
      const [data, error] = await ProjectService.reports.index(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async adminForceDeleteProject(id: Id) {
      const [data, error] = await ProjectService.admin.forceDelete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async adminRestoreProject(id: Id) {
      const [data, error] = await ProjectService.admin.restore(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async adminUpdateProject(id: Id, form: ApiRequests['api.v1.admin.projects.update']) {
      const [data, error] = await ProjectService.admin.update(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async commentaryIndex(id: Id) {
      const commentsStore = useCommentsStore();
      const [data, error] = await ProjectService.commentary.index(id);

      if(error) {
        throw error;
      }

      commentsStore.models.save(data);

      return data;
    },

    async commentaryStore(id: Id, form: ApiRequests['api.v1.projects.commentary.store']) {
      const commentsStore = useCommentsStore();
      const [data, error] = await ProjectService.commentary.store(id, form);

      if(error) {
        throw error;
      }

      commentsStore.models.save(data);

      return data;
    },

    async commentaryUpdate(projectId: Id, commentId: Id, form: ApiRequests['api.v1.projects.commentary.update']) {
      const commentsStore = useCommentsStore();
      const [data, error] = await ProjectService.commentary.update(projectId, commentId, form);

      if(error) {
        throw error;
      }

      commentsStore.models.save(data);

      return data;
    },

    async commentaryShow(projectId: Id, commentId: Id) {
      const commentsStore = useCommentsStore();
      const [data, error] = await ProjectService.commentary.show(projectId, commentId);

      if(error) {
        throw error;
      }

      commentsStore.models.save(data);

      return data;
    },

    async monthlyReport(id: Id, form: ApiRequests['api.v1.projects.monthly-report']) {
      const [data, error] = await ProjectService.reports.generate(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async costAggregationSummary(id: Id) {
      const [data, error] = await ProjectService.costAggregationSummary(id);

      if(error) {
        throw error;
      }

      return data;
    },

    async searchUsers(id: Id, form: ApiRequests['api.v1.projects.users.search']) {
      const [data, error] = await ProjectService.users.search(id, form);

      if(error) {
        throw error;
      }

      return data;
    },

    async archive(id: Id) {
      const [data, error] = await ProjectService.archive(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async unarchive(id: Id) {
      const [data, error] = await ProjectService.unarchive(id);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async updateSubcontractSettings(id: Id, form: ApiRequests['api.v1.projects.subcontract-settings']) {
      const [data, error] = await ProjectService.updateSubcontractSettings(id, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },
  },

  persist: {
    storage: sessionStorage,
  },
});
